import React, { useState, useEffect } from 'react';
import { X, Sparkles, Loader2, Mic, MicOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { getSystemPrompt, getContentTypeLabel } from './aiContentPrompts';

export interface ExistingSlideInfo {
  index: number;
  hasMedia: boolean;
  mediaType: 'image' | 'video';
}

interface AIContentAssistantProps {
  onClose: () => void;
  onContentGenerated: (content: string) => void;
  contentType?: 'post' | 'reel' | 'story';
  existingSlides?: ExistingSlideInfo[];
}

export const AIContentAssistant: React.FC<AIContentAssistantProps> = ({
  onClose,
  onContentGenerated,
  contentType = 'post',
  existingSlides,
}) => {
  const [userPrompt, setUserPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  
  const {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    error: speechError,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition();

  // Sync transcript with textarea
  useEffect(() => {
    if (transcript) {
      setUserPrompt(prev => {
        // If user has typed something, append with space
        if (prev && !prev.endsWith(' ') && !prev.endsWith('\n')) {
          return prev + ' ' + transcript;
        }
        return prev + transcript;
      });
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  // Show speech errors as toasts
  useEffect(() => {
    if (speechError) {
      toast.error(speechError);
    }
  }, [speechError]);

  const handleMicToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleGenerate = async () => {
    if (!userPrompt.trim()) {
      toast.error('Proszę opisać co chcesz stworzyć');
      return;
    }

    // Stop listening if recording
    if (isListening) {
      stopListening();
    }

    setIsGenerating(true);
    const webhookUrl = 'https://a.gregciupek.com/webhook/9837a335-98a3-4a31-9681-f5f7da6bfcdf';

    // Build enriched prompt with slide context if media slides exist
    const slidesWithMedia = existingSlides?.filter(s => s.hasMedia) || [];
    let enrichedPrompt = userPrompt;
    if (contentType === 'story' && slidesWithMedia.length > 0) {
      const mediaDesc = slidesWithMedia.map((s) => {
        const type = s.mediaType === 'video' ? 'wideo' : 'zdjęcie';
        return `Slajd ${s.index + 1}: ${type}`;
      }).join(', ');
      enrichedPrompt = `${userPrompt}\n\n[KONTEKST: Story składa się z ${slidesWithMedia.length} slajdów z mediami (${mediaDesc}). Wygeneruj tekst dokładnie do ${slidesWithMedia.length} slajdów, oddzielając je znakiem ---. Tekst będzie nałożony na istniejące media.]`;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 seconds timeout

      const systemPrompt = contentType === 'story' && slidesWithMedia.length > 0
        ? getSystemPrompt(contentType) + `\n\n## ISTNIEJĄCE SLAJDY Z MEDIAMI\nUżytkownik ma już ${slidesWithMedia.length} slajdów z wgranymi mediami. MUSISZ wygenerować tekst dokładnie do ${slidesWithMedia.length} slajdów oddzielonych "---". Tekst zostanie nałożony na istniejące zdjęcia/wideo — NIE twórz dodatkowych slajdów.`
        : getSystemPrompt(contentType);

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: enrichedPrompt,
          content_type: contentType,
          system_prompt: systemPrompt,
          timestamp: new Date().toISOString(),
          ...(slidesWithMedia.length > 0 ? { existing_slides_count: slidesWithMedia.length } : {}),
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Webhook error: ${response.status}`);
      }

      const data = await response.json();

      // Extract content from response
      let generatedContent = '';
      if (typeof data === 'string') {
        generatedContent = data;
      } else if (data.Task) {
        generatedContent = data.Task;
      } else if (data.task) {
        generatedContent = data.task;
      } else if (data.content) {
        generatedContent = data.content;
      } else if (data.text) {
        generatedContent = data.text;
      } else if (data.message) {
        generatedContent = data.message;
      } else {
        generatedContent = JSON.stringify(data);
      }

      if (generatedContent) {
        onContentGenerated(generatedContent);
        toast.success('Treść wygenerowana pomyślnie!');
        onClose();
      } else {
        throw new Error('Brak treści w odpowiedzi');
      }
    } catch (error: any) {
      console.error('AI Content Assistant error:', error);

      if (error.name === 'AbortError') {
        toast.error('Timeout: Agent AI nie odpowiedział w ciągu minuty');
      } else {
        toast.error('Błąd generowania treści: ' + error.message);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Display text with interim results shown in lighter color
  const displayText = userPrompt + (interimTranscript ? interimTranscript : '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                AI Content Assistant
              </h2>
              <p className="text-sm text-gray-600">
                Generowanie: <strong>{getContentTypeLabel(contentType)}</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isGenerating}
            className="p-2 hover:bg-white/50 rounded-full transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opisz co chcesz stworzyć
              </label>
              
              {/* Textarea with microphone button */}
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <textarea
                    value={displayText}
                    onChange={(e) => setUserPrompt(e.target.value)}
                    disabled={isGenerating}
                    className={`w-full h-48 p-4 border rounded-lg resize-none 
                      disabled:bg-gray-50 disabled:cursor-not-allowed
                      focus:ring-2 focus:border-transparent transition-all
                      ${isListening 
                        ? 'border-red-400 ring-2 ring-red-200 focus:ring-red-300' 
                        : 'border-gray-300 focus:ring-blue-500'
                      }`}
                    placeholder={contentType === 'reel'
                      ? "Np. 'Stwórz opis do Reela o nowym produkcie - ekologiczna szczoteczka do zębów z bambusa. Reel pokazuje unboxing. Opis krótki, angażujący, z emoji i hashtagami.'"
                      : contentType === 'story'
                      ? "Np. 'Stwórz tekst do Story z ogłoszeniem promocji -50% na całą kolekcję. Story powinno być krótkie i zachęcające do kliknięcia w link.'"
                      : "Np. 'Stwórz post o nowym produkcie - ekologiczna szczoteczka do zębów z bambusa. Post powinien być entuzjastyczny i zachęcający do zakupu. Dodaj emoji i CTA.'"}
                  />
                  
                  {/* Interim transcript overlay indicator */}
                  {interimTranscript && (
                    <div className="absolute bottom-3 left-4 right-4 text-sm text-gray-400 pointer-events-none truncate">
                      {interimTranscript}
                    </div>
                  )}
                </div>
                
                {/* Microphone button - only show if supported */}
                {isSupported && (
                  <button
                    onClick={handleMicToggle}
                    disabled={isGenerating}
                    className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center
                      transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                      ${isListening
                        ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-200'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800'
                      }
                      ${isListening ? 'animate-pulse' : ''}
                    `}
                    aria-label={isListening ? 'Zatrzymaj nagrywanie' : 'Rozpocznij dyktowanie'}
                    title={isListening ? 'Zatrzymaj nagrywanie' : 'Dyktuj głosowo'}
                  >
                    {isListening ? (
                      <MicOff className="w-6 h-6" />
                    ) : (
                      <Mic className="w-6 h-6" />
                    )}
                  </button>
                )}
              </div>
              
              {/* Recording indicator */}
              {isListening && (
                <div className="mt-2 flex items-center gap-2 text-red-600">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  <span className="text-sm font-medium">Nagrywanie... Mów teraz</span>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                Wskazówki:
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Opisz temat posta</li>
                <li>• Określ ton komunikacji (formalny/casualowy)</li>
                <li>• Wspomnij o dodatkach (emoji, hashtagi, CTA)</li>
                <li>• Podaj długość treści jeśli ważna</li>
                {isSupported && (
                  <li className="pt-1 border-t border-blue-200 mt-2">
                    • 🎤 <strong>Dyktowanie:</strong> Kliknij mikrofon i mów wyraźnie
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100
                focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !userPrompt.trim()}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg
                hover:from-blue-700 hover:to-purple-700
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center gap-2 transition-all"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Generowanie...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Generuj treść</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
